import { ProcurementBatchItem, ProcurementBatch } from '../types';

export type GroupingType = 'none' | 'category' | 'brand';

export interface GroupingOptions {
  groupBy: GroupingType;
  hideChecked: boolean;
  showOnlyIncomplete: boolean;
  selectedCategories: string[];
  selectedBrands: string[];
}

export interface ItemGroup {
  name: string;
  items: ProcurementBatchItem[];
  totalItems: number;
  checkedItems: number;
  completedItems: number;
  incompleteItems: number;
  collectionPercentage: number;
}

export interface GroupedItems {
  groups: ItemGroup[];
  totalItems: number;
  hasGroups: boolean;
}

/**
 * Groups procurement batch items by category
 */
export function groupItemsByCategory(items: ProcurementBatchItem[]): Map<string, ProcurementBatchItem[]> {
  const groups = new Map<string, ProcurementBatchItem[]>();
  
  items.forEach(item => {
    const category = item.category || 'Uncategorized';
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(item);
  });
  
  return groups;
}

/**
 * Groups procurement batch items by brand
 */
export function groupItemsByBrand(items: ProcurementBatchItem[]): Map<string, ProcurementBatchItem[]> {
  const groups = new Map<string, ProcurementBatchItem[]>();
  
  items.forEach(item => {
    const brand = item.brand || 'Unknown Brand';
    if (!groups.has(brand)) {
      groups.set(brand, []);
    }
    groups.get(brand)!.push(item);
  });
  
  return groups;
}

/**
 * Gets all unique categories from a list of items
 */
export function getAvailableCategories(items: ProcurementBatchItem[]): string[] {
  const categories = new Set<string>();
  
  items.forEach(item => {
    const category = item.category || 'Uncategorized';
    categories.add(category);
  });
  
  return Array.from(categories).sort();
}

/**
 * Gets all unique brands from a list of items
 */
export function getAvailableBrands(items: ProcurementBatchItem[]): string[] {
  const brands = new Set<string>();
  
  items.forEach(item => {
    const brand = item.brand || 'Unknown Brand';
    brands.add(brand);
  });
  
  return Array.from(brands).sort();
}

/**
 * Helper function to check if an item is checked
 */
function isItemChecked(item: ProcurementBatchItem, checkedItems: string[]): boolean {
  return checkedItems.includes(item.productId);
}

/**
 * Helper function to check if an item is completed (has sufficient actual quantity)
 */
function isItemCompleted(item: ProcurementBatchItem, actualQuantities: { [key: string]: number }): boolean {
  const actualQuantity = actualQuantities[item.productId] || 0;
  return actualQuantity >= item.requiredQuantity;
}

/**
 * Helper function to check if an item is incomplete (has some but insufficient actual quantity)
 */
function isItemIncomplete(item: ProcurementBatchItem, actualQuantities: { [key: string]: number }): boolean {
  const actualQuantity = actualQuantities[item.productId] || 0;
  return actualQuantity > 0 && actualQuantity < item.requiredQuantity;
}

/**
 * Filters items based on the current filter settings
 */
function applyFilters(
  items: ProcurementBatchItem[], 
  options: GroupingOptions,
  checkedItems: string[],
  actualQuantities: { [key: string]: number }
): ProcurementBatchItem[] {
  let filteredItems = items;

  // Apply category filter
  if (options.selectedCategories.length > 0) {
    filteredItems = filteredItems.filter(item => {
      const category = item.category || 'Uncategorized';
      return options.selectedCategories.includes(category);
    });
  }

  // Apply brand filter
  if (options.selectedBrands.length > 0) {
    filteredItems = filteredItems.filter(item => {
      const brand = item.brand || 'Unknown Brand';
      return options.selectedBrands.includes(brand);
    });
  }

  // Apply incomplete items filter
  if (options.showOnlyIncomplete) {
    filteredItems = filteredItems.filter(item => {
      return isItemIncomplete(item, actualQuantities);
    });
  }

  // Apply hide checked filter
  if (options.hideChecked) {
    filteredItems = filteredItems.filter(item => {
      return !isItemChecked(item, checkedItems);
    });
  }

  return filteredItems;
}

/**
 * Creates an ItemGroup with calculated statistics
 */
function createItemGroup(
  name: string, 
  items: ProcurementBatchItem[], 
  checkedItems: string[],
  actualQuantities: { [key: string]: number }
): ItemGroup {
  const totalItems = items.length;
  const checkedCount = items.filter(item => isItemChecked(item, checkedItems)).length;
  const completedCount = items.filter(item => isItemCompleted(item, actualQuantities)).length;
  const incompleteCount = items.filter(item => isItemIncomplete(item, actualQuantities)).length;
  const collectionPercentage = totalItems > 0 ? (completedCount / totalItems) * 100 : 0;

  return {
    name,
    items,
    totalItems,
    checkedItems: checkedCount,
    completedItems: completedCount,
    incompleteItems: incompleteCount,
    collectionPercentage: Math.round(collectionPercentage)
  };
}

/**
 * Sorts items within a group (unchecked first, then checked)
 */
function sortItemsInGroup(items: ProcurementBatchItem[], checkedItems: string[]): ProcurementBatchItem[] {
  const uncheckedItems = items.filter(item => !isItemChecked(item, checkedItems));
  const checkedGroupItems = items.filter(item => isItemChecked(item, checkedItems));
  
  return [...uncheckedItems, ...checkedGroupItems];
}

/**
 * Main function that applies grouping and filtering to procurement batch items
 */
export function applyCombinedGroupingAndFiltering(
  items: ProcurementBatchItem[],
  options: GroupingOptions,
  checkedItems: string[],
  actualQuantities: { [key: string]: number }
): GroupedItems {
  // First apply filters to get the filtered items
  const filteredItems = applyFilters(items, options, checkedItems, actualQuantities);

  // If no grouping is selected, return items as a single group
  if (options.groupBy === 'none') {
    const sortedItems = sortItemsInGroup(filteredItems, checkedItems);
    return {
      groups: [],
      totalItems: sortedItems.length,
      hasGroups: false
    };
  }

  // Group items based on the selected grouping type
  let groupMap: Map<string, ProcurementBatchItem[]>;
  
  if (options.groupBy === 'category') {
    groupMap = groupItemsByCategory(filteredItems);
  } else if (options.groupBy === 'brand') {
    groupMap = groupItemsByBrand(filteredItems);
  } else {
    // Fallback to no grouping
    const sortedItems = sortItemsInGroup(filteredItems, checkedItems);
    return {
      groups: [],
      totalItems: sortedItems.length,
      hasGroups: false
    };
  }

  // Convert map to array of ItemGroups and calculate statistics
  const groups: ItemGroup[] = Array.from(groupMap.entries())
    .map(([groupName, groupItems]) => {
      const sortedItems = sortItemsInGroup(groupItems, checkedItems);
      return createItemGroup(groupName, sortedItems, checkedItems, actualQuantities);
    })
    .filter(group => group.totalItems > 0) // Remove empty groups
    .sort((a, b) => {
      // Sort groups by completion percentage (incomplete first), then by name
      if (a.collectionPercentage !== b.collectionPercentage) {
        return a.collectionPercentage - b.collectionPercentage;
      }
      return a.name.localeCompare(b.name);
    });

  const totalItems = groups.reduce((sum, group) => sum + group.totalItems, 0);

  return {
    groups,
    totalItems,
    hasGroups: true
  };
}

/**
 * Gets display name for a grouping type
 */
export function getGroupingDisplayName(groupBy: GroupingType): string {
  switch (groupBy) {
    case 'category':
      return 'Category';
    case 'brand':
      return 'Brand';
    case 'none':
    default:
      return 'None';
  }
}

/**
 * Formats group header text with item counts
 */
export function formatGroupHeaderText(group: ItemGroup): string {
  const completionText = group.totalItems > 0 
    ? `${group.completedItems} of ${group.totalItems} collected`
    : 'No items';
  
  return `${group.name} (${completionText})`;
}

/**
 * Gets group completion status for styling
 */
export function getGroupCompletionStatus(group: ItemGroup): 'completed' | 'partial' | 'incomplete' {
  if (group.completedItems === group.totalItems && group.totalItems > 0) {
    return 'completed';
  } else if (group.completedItems > 0) {
    return 'partial';
  } else {
    return 'incomplete';
  }
}

/**
 * Checks if a group should be expanded by default
 * Groups with incomplete items are expanded by default
 */
export function shouldGroupBeExpandedByDefault(group: ItemGroup): boolean {
  return group.incompleteItems > 0 || group.completedItems < group.totalItems;
}

/**
 * Gets all items from grouped results (flattened)
 */
export function getFlattenedItemsFromGroups(groupedResult: GroupedItems): ProcurementBatchItem[] {
  if (!groupedResult.hasGroups) {
    return [];
  }
  
  return groupedResult.groups.reduce((allItems, group) => {
    return allItems.concat(group.items);
  }, [] as ProcurementBatchItem[]);
}